import { authedJson } from './client';
import { API_ROUTES } from '../../../shared/api';
import type {
  BookDetail,
  ChapterDetail,
  Deck,
  LanguageDetail,
  LanguagesResponse,
  SectionDetail,
} from '../../../shared/types';

type Auth = { getToken: () => string | null; doRefresh: () => Promise<boolean> };

/**
 * The catalog is static and immutable for the life of a deploy, so a fetched
 * section is cached in memory for the rest of the session. Flipping between the
 * Study and Exercises tabs, or stepping back from a drill, then costs nothing.
 */
const cache = new Map<string, unknown>();

async function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit !== undefined) return hit as T;
  const value = await load();
  cache.set(key, value);
  return value;
}

/** Dropped on sign-out so a second account never sees the first one's cache. */
export function clearCatalogCache(): void {
  cache.clear();
}

export const catalogApi = {
  languages: ({ getToken, doRefresh }: Auth) =>
    cached('languages', () =>
      authedJson<LanguagesResponse>(API_ROUTES.languages, { method: 'GET' }, getToken, doRefresh),
    ),

  language: (code: string, { getToken, doRefresh }: Auth) =>
    cached(`language:${code}`, () =>
      authedJson<LanguageDetail>(API_ROUTES.language(code), { method: 'GET' }, getToken, doRefresh),
    ),

  book: (bookId: string, { getToken, doRefresh }: Auth) =>
    cached(`book:${bookId}`, () =>
      authedJson<BookDetail>(API_ROUTES.book(bookId), { method: 'GET' }, getToken, doRefresh),
    ),

  chapter: (chapterId: string, { getToken, doRefresh }: Auth) =>
    cached(`chapter:${chapterId}`, () =>
      authedJson<ChapterDetail>(API_ROUTES.chapter(chapterId), { method: 'GET' }, getToken, doRefresh),
    ),

  section: (sectionId: string, { getToken, doRefresh }: Auth) =>
    cached(`section:${sectionId}`, () =>
      authedJson<SectionDetail>(API_ROUTES.section(sectionId), { method: 'GET' }, getToken, doRefresh),
    ),

  /**
   * One deck built from several sections. Cached on the sorted id list, so
   * re-running the same custom session costs nothing while a different pick
   * misses cleanly.
   */
  deck: (sectionIds: string[], { getToken, doRefresh }: Auth) =>
    cached(`deck:${[...sectionIds].sort().join(',')}`, () =>
      authedJson<Deck>(API_ROUTES.deck(sectionIds), { method: 'GET' }, getToken, doRefresh),
    ),
};
